import { useMemo, useRef, useState } from 'react'

export const useScrollLock = (defaultState = false) => {
  const [isLocked, setIsLocked] = useState(defaultState)

  const snapshotValues = useRef<{ scrollTop: number; marginRight: string; scrollbarGutter: string }>({
    scrollTop: 0,
    marginRight: '',
    scrollbarGutter: ''
  })

  return useMemo(
    () => ({
      lockScroll: () => {
        if (typeof document === 'undefined') {
          return
        }

        const htmlNode = document.querySelector('html')
        const bodyNode = document.querySelector('body')

        if (!htmlNode || !bodyNode) {
          return
        }

        setIsLocked(true)

        snapshotValues.current.scrollTop = document.documentElement.scrollTop
        snapshotValues.current.scrollbarGutter = htmlNode.style.scrollbarGutter
        snapshotValues.current.marginRight = htmlNode.style.marginRight

        if (CSS.supports('scrollbar-gutter: stable')) {
          // Prevents page shifting when scroll bar is removed
          htmlNode.style.scrollbarGutter = 'stable'
        } else {
          // Hack to mimic scrollbar width
          // This is to prevent page shifting since the scroll bar will be removed
          htmlNode.style.marginRight = `${window.innerWidth - document.documentElement.clientWidth}px`
        }

        // Need to set the class name on next screen paint so that previous style can be applied
        window.requestAnimationFrame(() => {
          htmlNode.classList.add('scrollLock')
          bodyNode.classList.add('scrollLock')
        })
      },
      unlockScroll: () => {
        if (!isLocked || typeof document === 'undefined') {
          setIsLocked(false)

          return
        }

        const htmlNode = document.querySelector('html')

        if (htmlNode) {
          htmlNode.classList.remove('scrollLock')
          htmlNode.style.scrollbarGutter = snapshotValues.current.scrollbarGutter
          htmlNode.style.marginRight = snapshotValues.current.marginRight
        }

        document.querySelector('body')?.classList.remove('scrollLock')

        // Reset scroll position on next paint to ensure CSS has been updated
        window.requestAnimationFrame(() => {
          document.documentElement.scrollTop = snapshotValues.current.scrollTop
          setIsLocked(false)
        })
      }
    }),
    [isLocked, setIsLocked]
  )
}
